export type PrimitiveListRowItem = {
  compounds: string[];
  gloss: string | null;
  glyph: string;
};

export type PrimitiveVirtualRow = {
  item: PrimitiveListRowItem;
  key: string;
  rank: number;
} & (
  | { expanded: boolean; kind: "header" }
  | { kind: "compounds"; offset: number }
  | { kind: "footer" }
);

export const PRIMITIVE_GRID_GAP = 4;
export const PRIMITIVE_COLLAPSED_ROWS = 2;

export function getPrimitiveGridMetrics(windowWidth: number, web: boolean) {
  // The list and each surface body have 4px padding on either side.
  const availableWidth = Math.max(0, windowWidth - 16);
  const columns = web
    ? Math.max(4, Math.floor((availableWidth + 4) / 52))
    : getSurfaceTileGridLayout(availableWidth, 0).glyphColumns;
  return {
    availableWidth,
    cellSize: Math.max(0, (availableWidth - PRIMITIVE_GRID_GAP * (columns - 1)) / columns),
    columns,
  };
}

/**
 * Only descriptors enter the virtualizer. Expanded cards never materialize another copy of their
 * compounds: a mounted row slices at most `columns` strings from the original ordered query data.
 */
export function createPrimitiveVirtualRows(
  primitives: readonly PrimitiveListRowItem[],
  expandedGlyphs: ReadonlySet<string>,
  columns: number,
): PrimitiveVirtualRow[] {
  const rows: PrimitiveVirtualRow[] = [];
  for (const [index, item] of primitives.entries()) {
    const rank = index + 1;
    const expanded =
      expandedGlyphs.has(item.glyph) && item.compounds.length > PRIMITIVE_COLLAPSED_ROWS * columns;
    rows.push({ expanded, item, key: `${item.glyph}:header`, kind: "header", rank });
    if (!expanded) {
      continue;
    }
    for (let offset = 0; offset < item.compounds.length; offset += columns) {
      rows.push({
        item,
        key: `${item.glyph}:compounds:${offset}`,
        kind: "compounds",
        offset,
        rank,
      });
    }
    rows.push({ item, key: `${item.glyph}:footer`, kind: "footer", rank });
  }
  return rows;
}

export function getPrimitiveRowGlyphs(row: PrimitiveVirtualRow, columns: number): string[] {
  if (row.kind === "footer") {
    return [];
  }
  if (row.kind === "compounds") {
    return row.item.compounds.slice(row.offset, row.offset + columns);
  }
  if (row.expanded) {
    return [row.item.glyph];
  }
  const maximum = PRIMITIVE_COLLAPSED_ROWS * columns;
  return [
    row.item.glyph,
    ...row.item.compounds.slice(0, row.item.compounds.length > maximum ? maximum - 1 : maximum),
  ];
}
import { getSurfaceTileGridLayout } from "@-/dev.improve.haniverse.components.ui.surface/grid-layout";
